import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Modal de signature pour refus d'EPI (protections auditives ou chaussures de sécurité)
 * Mode création : case à cocher d'accusé, date, zone de signature canvas
 * Mode consultation : affiche la signature existante en lecture seule
 */
const EPI_LABELS = {
  hearing: {
    title: 'Refus de protection auditive',
    epiName: 'des protections auditives',
    checkboxText: 'Je suis conscient des risques en cas de non-port des EPI (protections auditives)'
  },
  shoes: {
    title: 'Refus de chaussures de sécurité',
    epiName: 'des chaussures de sécurité',
    checkboxText: 'Je suis conscient des risques en cas de non-port des EPI (chaussures de sécurité)'
  }
};

const EPIRefusalModal = ({ userName, onConfirm, onCancel, viewData, onDelete, epiType = 'hearing' }) => {
  const labels = EPI_LABELS[epiType] || EPI_LABELS.hearing;
  const [acknowledged, setAcknowledged] = useState(false);
  const [signatureDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  // Mode consultation
  const isViewMode = !!viewData;

  // Init canvas (mode création uniquement)
  useEffect(() => {
    if (isViewMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [isViewMode]);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSigned(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }, [isDrawing, getPos]);

  const stopDraw = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setHasSigned(false);
  }, []);

  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onConfirm({
      signature: signatureData,
      date: signatureDate,
      acknowledged: true
    });
  }, [onConfirm, signatureDate]);

  const isValid = acknowledged && hasSigned;

  // Formater la date pour l'affichage
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + (dateStr.length === 10 ? 'T12:00:00' : ''));
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Mode consultation
  if (isViewMode) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          background: 'white', borderRadius: '12px', padding: '24px',
          maxWidth: '480px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}>
          <h3 style={{ margin: '0 0 16px', color: '#dc3545', fontSize: '16px' }}>
            {labels.title}
          </h3>

          <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#495057' }}>
            <strong>{userName}</strong> a refusé le port {labels.epiName}.
          </p>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            margin: '0 0 16px', fontSize: '13px', color: '#333',
            background: '#f8d7da', padding: '12px', borderRadius: '8px', border: '1px solid #f5c6cb'
          }}>
            <span style={{ fontSize: '16px' }}>&#9888;</span>
            <span>Risques reconnus en cas de non-port des EPI</span>
          </div>

          <div style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>
            Date de signature : <strong>{formatDate(viewData.date)}</strong>
          </div>

          {viewData.signature && (
            <div style={{ margin: '0 0 16px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '6px' }}>Signature :</span>
              <img
                src={viewData.signature}
                alt="Signature de refus EPI"
                style={{
                  width: '100%', height: '150px', objectFit: 'contain',
                  border: '2px solid #dee2e6', borderRadius: '8px', background: '#fff'
                }}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                style={{
                  padding: '10px 20px', fontSize: '13px', fontWeight: '600',
                  background: '#fff', color: '#dc3545', border: '1px solid #dc3545',
                  borderRadius: '8px', cursor: 'pointer'
                }}
              >
                Supprimer le refus
              </button>
            )}
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 20px', fontSize: '13px', fontWeight: '600',
                background: '#6c757d', color: 'white', border: 'none',
                borderRadius: '8px', cursor: 'pointer'
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mode création
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: 'white', borderRadius: '12px', padding: '24px',
        maxWidth: '480px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <h3 style={{ margin: '0 0 16px', color: '#dc3545', fontSize: '16px' }}>
          {labels.title}
        </h3>

        <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#495057' }}>
          <strong>{userName}</strong> refuse le port {labels.epiName} pour cette journée.
        </p>

        {/* Case à cocher */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          margin: '0 0 16px', cursor: 'pointer', fontSize: '13px', color: '#333',
          background: '#fff3cd', padding: '12px', borderRadius: '8px', border: '1px solid #ffc107'
        }}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            style={{ marginTop: '2px', width: '18px', height: '18px', flexShrink: 0 }}
          />
          <span>{labels.checkboxText}</span>
        </label>

        {/* Date */}
        <div style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>
          Date : <strong>{formatDate(signatureDate)}</strong>
        </div>

        {/* Zone signature */}
        <div style={{ margin: '0 0 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>Signature :</span>
            <button
              type="button"
              onClick={clearSignature}
              style={{
                padding: '4px 10px', fontSize: '11px', background: '#f8f9fa',
                border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer'
              }}
            >
              Effacer
            </button>
          </div>
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            style={{
              width: '100%', height: '150px',
              border: '2px solid ' + (hasSigned ? '#28a745' : '#dee2e6'),
              borderRadius: '8px', cursor: 'crosshair',
              touchAction: 'none', background: '#fff'
            }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
          />
        </div>

        {/* Boutons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px', fontSize: '13px', fontWeight: '600',
              background: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6',
              borderRadius: '8px', cursor: 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValid}
            style={{
              padding: '10px 20px', fontSize: '13px', fontWeight: '600',
              background: isValid ? '#dc3545' : '#e9ecef',
              color: isValid ? 'white' : '#adb5bd',
              border: 'none', borderRadius: '8px',
              cursor: isValid ? 'pointer' : 'not-allowed'
            }}
          >
            Confirmer le refus
          </button>
        </div>
      </div>
    </div>
  );
};

export default EPIRefusalModal;
